import React, { createContext, useState, useContext, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';

const AuthContext = createContext();

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

// Set up axios interceptor to include token in all requests
function setupAxiosAuth() {
  axios.interceptors.request.use((config) => {
    const token = localStorage.getItem('access_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  });
}
setupAxiosAuth();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    const token = localStorage.getItem('access_token');
    if (!token) {
      setUser(false);
      setLoading(false);
      return;
    }
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/auth/me`, {
        withCredentials: true
      });
      // If server returns a new token (refreshed), store it
      if (data.token) {
        localStorage.setItem('access_token', data.token);
      }
      setUser(data);
    } catch {
      localStorage.removeItem('access_token');
      setUser(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password) => {
    try {
      const { data } = await axios.post(
        `${BACKEND_URL}/api/auth/login`,
        { email, password },
        { withCredentials: true }
      );
      if (data.token) {
        localStorage.setItem('access_token', data.token);
      }
      setUser(data);
      return { success: true };
    } catch (error) {
      return { 
        success: false, 
        error: formatApiErrorDetail(error.response?.data?.detail) || error.message 
      };
    }
  };

  const register = async (name, email, password) => {
    try {
      const { data } = await axios.post(
        `${BACKEND_URL}/api/auth/register`,
        { name, email, password },
        { withCredentials: true }
      );
      if (data.token) {
        localStorage.setItem('access_token', data.token);
      }
      setUser(data);
      return { success: true };
    } catch (error) {
      return { 
        success: false, 
        error: formatApiErrorDetail(error.response?.data?.detail) || error.message 
      };
    }
  };

  const logout = async () => {
    try {
      await axios.post(
        `${BACKEND_URL}/api/auth/logout`,
        {},
        { withCredentials: true }
      );
    } catch {
      // Logout failed silently
    }
    localStorage.removeItem('access_token');
    setUser(false);
  };

  const contextValue = useMemo(() => ({
    user, loading, login, register, logout, checkAuth,
    impersonate: async (userId) => {
      try {
        const token = localStorage.getItem('access_token');
        const { data } = await axios.post(`${BACKEND_URL}/api/admin/impersonate/${userId}`, {}, { withCredentials: true });
        localStorage.setItem('admin_token', token);
        localStorage.setItem('access_token', data.token);
        localStorage.setItem('impersonating', JSON.stringify({ name: data.name, email: data.email }));
        await checkAuth();
        return { success: true };
      } catch (error) {
        return { success: false, error: error.response?.data?.detail || error.message };
      }
    },
    exitImpersonation: async () => {
      const adminToken = localStorage.getItem('admin_token');
      if (adminToken) {
        localStorage.setItem('access_token', adminToken);
        localStorage.removeItem('admin_token');
        localStorage.removeItem('impersonating');
        // Re-authenticate as admin
        try {
          const { data } = await axios.get(`${BACKEND_URL}/api/auth/me`, { withCredentials: true });
          if (data.token) {
            localStorage.setItem('access_token', data.token);
          }
          setUser(data);
        } catch {
          setUser(null);
        }
      }
    },
    isImpersonating: !!localStorage.getItem('impersonating'),
    impersonatingUser: (() => { try { return JSON.parse(localStorage.getItem('impersonating')); } catch { return null; } })()
  }), [user, loading, checkAuth]);

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};
